/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { SPORTS_PAY_MARKETING } from '@sportninja/common/features/account/sportsPaySignUp'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import React from 'react'
import { TITLE_LETTER_SPACING } from '../constants'
import CanlanLogoIcon from 'src/components/icons/CanlanLogoIcon'
import CanlanTextLogo from 'src/components/icons/CanlanTextLogo'
import Text from 'src/components/ts-components/Text'

/** Shared SportsPay mark, already used by the Payment Providers screen. */
const SPORTS_PAY_LOGO_SRC = '/images/sportspay.png'

/** Existing desaturated arena shot, reused as the panel's sports backdrop. */
const SPORTS_BACKDROP_SRC = '/images/game_hockey.png'

/**
 * Left-hand marketing panel of the SportsPay signup modal — Canlan branding in
 * the top-left corner, the SportsPay mark, headline, supporting copy and a
 * highlight card of bullets, over a faded SportsPay backdrop.
 */
const SportsPayMarketingPanel: React.FC = () => {
  return (
    <div css={panelStyle}>
      {/* Faded backdrop — decorative only, kept behind the content */}
      <div aria-hidden css={backdropStyle} />

      <div css={contentStyle}>
        <div css={canlanBrandStyle}>
          <CanlanLogoIcon height={24} />
          <CanlanTextLogo height={12} color={colors.NEUTRAL_0} />
        </div>

        <div css={brandStyle}>
          <img src={SPORTS_PAY_LOGO_SRC} alt='SportsPay' css={logoImageStyle} />
          <Text
            variant='heading3'
            weight='bold'
            color={colors.NEUTRAL_0}
            style={TITLE_LETTER_SPACING}
          >
            SportsPay
          </Text>
        </div>

        <div css={copyStyle}>
          <Text
            variant='heading5'
            weight='semiBold'
            color={colors.NEUTRAL_0}
            style={TITLE_LETTER_SPACING}
          >
            {capitalizeWords(SPORTS_PAY_MARKETING.title)}
          </Text>

          {SPORTS_PAY_MARKETING.paragraphs.map((paragraph) => (
            <Text
              key={paragraph}
              variant='paragraphSmall'
              weight='regular'
              color={colors.NEUTRAL_0}
            >
              {paragraph}
            </Text>
          ))}
        </div>

        <div css={bulletCardStyle}>
          {SPORTS_PAY_MARKETING.bullets.map((bullet) => (
            <Text
              key={bullet}
              variant='paragraphSmall'
              weight='regular'
              color={colors.NEUTRAL_0}
            >
              ✅ {bullet}
            </Text>
          ))}
        </div>
      </div>
    </div>
  )
}

export default SportsPayMarketingPanel

/* -------------------------------------------------------------------------- */
/*                                   Styles                                   */
/* -------------------------------------------------------------------------- */

const panelStyle = css`
  position: relative;
  flex: 1;
  overflow: hidden;
  background-color: ${colors.SECONDARY_500};
  border-radius: 16px 0 0 16px;
  padding: 40px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  /* Hidden below 1100px: at narrower widths the 27-field application needs the
     whole modal, and the marketing copy is the expendable half. */
  @media (max-width: 1100px) {
    display: none;
  }
`

/**
 * Faded arena backdrop. The image sits at low opacity over the dark panel
 * colour, with a gradient scrim on top so the copy keeps its contrast — the
 * source shot is brightest through its middle band, exactly where the headline
 * and paragraphs sit.
 */
const backdropStyle = css`
  position: absolute;
  inset: 0;
  background-image: linear-gradient(
      180deg,
      ${colors.SECONDARY_500} 0%,
      rgba(24, 29, 38, 0.65) 45%,
      ${colors.SECONDARY_500} 100%
    ),
    url('${SPORTS_BACKDROP_SRC}');
  background-repeat: no-repeat, no-repeat;
  background-position: center, center;
  background-size: cover, cover;
  opacity: 0.55;
  pointer-events: none;
`

const contentStyle = css`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 24px;
  height: 100%;
`

/** Canlan branding pinned to the panel's top-left corner. */
const canlanBrandStyle = css`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`

const brandStyle = css`
  display: flex;
  align-items: center;
  gap: 12px;
`

const logoImageStyle = css`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: contain;
`

const copyStyle = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const bulletCardStyle = css`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(5px);
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`
